import type {
  CurrentSelectedRole,
  GovAgencyItems,
  RoleAssignmentItem,
  RolesAssignment,
  RolesToAssign,
  SingleGovAgencyItem,
} from 'modules/account/types';
import { defaultRolesToAssign } from 'modules/account/services/useCreateAccount';
import { findByKey } from 'modules/account/detail/helpers/formatters';

export const shouldShowCustomerList = (current: CurrentSelectedRole) => {
  if (!current || current.role.parentId !== null) return false;
  return current.rolesType !== 'systemRoles';
};

export const addClient = (clients: RoleAssignmentItem[], newClient: RoleAssignmentItem) => [
  ...clients,
  newClient,
];

export const removeClient = (customers: RoleAssignmentItem[], client: RoleAssignmentItem) => {
  const newCustomers = [...customers];
  const clientToRemove = newCustomers.find(
    (c) => c.orgId === client.orgId && c.roleId === client.roleId
  );

  if (!clientToRemove) return newCustomers;
  newCustomers.splice(newCustomers.indexOf(clientToRemove), 1);
  return newCustomers;
};

/**
 * Normalize roleType to rolesAssignment key.
 * Chỉ sử dụng cho vai trò nghiệp vụ và vai trò quản trị.
 */
export const convertRoles = (roleType: string) => {
  const { rolesAssignment } = defaultRolesToAssign;
  if (Object.keys(rolesAssignment).includes(roleType)) return roleType;
  if (roleType === 'adminRoles') return 'forAdminRoles';
  if (roleType === 'businessRoles') return 'forClientBusiness';
  throw Error(`invalid roleType: ${roleType}`);
};

export const setCustomersByRoleType = (
  roleType: string,
  rolesToAssign: RolesToAssign,
  isChecked: boolean,
  roleId: string
): RolesToAssign => {
  const remainingRoles = { ...rolesToAssign };
  const targetRoleType = convertRoles(roleType);

  const tempRemoved = remainingRoles.rolesAssignment[targetRoleType].filter(
    (item) => item.roleId !== roleId
  );

  const allCustomers = remainingRoles.customersSource.map(({ operationCode: orgId }) => ({
    roleId,
    orgId,
  }));

  remainingRoles.rolesAssignment[targetRoleType] = isChecked
    ? [...allCustomers, ...tempRemoved]
    : tempRemoved;

  return remainingRoles;
};

export const countCustomers = <K extends keyof RolesAssignment>(
  targetRole: RolesAssignment[K] & (RoleAssignmentItem[] | GovAgencyItems) = [],
  targetRoleId: string
): number => {
  return targetRole.filter(({ roleId }) => roleId === targetRoleId).length;
};

export const addAllClientsForNewGov = (r: RolesToAssign, roleItem: RoleAssignmentItem) => {
  const remainingRoles = { ...r };
  const { customersSource, rolesAssignment } = remainingRoles;

  const newGovRole = {
    ...roleItem,
    includedClientIds: customersSource.map((client) => client.operationCode),
    includedClientByBoundary: false,
  };

  remainingRoles.rolesAssignment.forGovAgencyBusiness = [
    ...rolesAssignment.forGovAgencyBusiness,
    newGovRole,
  ];

  return remainingRoles;
};

export const addAllOrRemoveAllClientsId = (
  r: RolesToAssign,
  govRole: SingleGovAgencyItem,
  shouldAddAll: boolean
) => {
  const remainingRoles = { ...r };
  const { customersSource, rolesAssignment } = remainingRoles;
  const otherGovAgencyRoles = rolesAssignment.forGovAgencyBusiness.filter(
    ({ roleId }) => roleId !== govRole.roleId
  );

  if (shouldAddAll) {
    const roleToEdit = { ...govRole };
    roleToEdit.includedClientIds = customersSource.map(({ operationCode }) => operationCode);
    remainingRoles.rolesAssignment.forGovAgencyBusiness = [...otherGovAgencyRoles, roleToEdit];
  } else {
    remainingRoles.rolesAssignment.forGovAgencyBusiness = otherGovAgencyRoles;
  }

  return remainingRoles;
};

export const updateSystemRoles = (
  r: RolesToAssign,
  add: boolean,
  roleAssigment: RoleAssignmentItem
): RolesToAssign => {
  const remainingRoles = { ...r };
  const systemsRoles = remainingRoles.rolesAssignment.forSystemRoles;
  const shouldAdd = add && !findByKey(systemsRoles, roleAssigment.roleId);
  const updatedForSystemRoles = shouldAdd
    ? [...systemsRoles, roleAssigment]
    : systemsRoles.filter((role) => role.roleId !== roleAssigment.roleId);

  remainingRoles.rolesAssignment.forSystemRoles = updatedForSystemRoles;
  return remainingRoles;
};

export const addCqqlWithAClientUnder = (
  r: RolesToAssign,
  clientId: string,
  roleAssigment: RoleAssignmentItem
) => {
  const newRoles = { ...r };
  newRoles.rolesAssignment.forGovAgencyBusiness = [
    ...newRoles.rolesAssignment.forGovAgencyBusiness,
    {
      ...roleAssigment,
      includedClientIds: [clientId],
      includedClientByBoundary: false,
    },
  ];
  return newRoles;
};

/**
 * Updates includedClientIds for a CQQL gov role. Add or remove a client.
 * If includedClientIds becomes empty, the gov role is removed.
 */
export const updateIncludedClientIds = (
  rolesToAssign: RolesToAssign,
  roleToEdit: SingleGovAgencyItem,
  add: boolean,
  clientId: string
): RolesToAssign => {
  const newRoles = { ...rolesToAssign };
  const govRole = { ...roleToEdit };

  govRole.includedClientIds = add
    ? [...(govRole.includedClientIds || []), clientId]
    : (govRole.includedClientIds || []).filter((id) => id !== clientId);

  const otherGovAgencyRoles = newRoles.rolesAssignment.forGovAgencyBusiness.filter(
    ({ roleId }) => roleId !== govRole.roleId
  );

  newRoles.rolesAssignment.forGovAgencyBusiness =
    (govRole.includedClientIds?.length ?? 0) === 0
      ? otherGovAgencyRoles
      : [...otherGovAgencyRoles, govRole];

  return newRoles;
};

export const addNewGovRole = (r: RolesToAssign, newRole: SingleGovAgencyItem): RolesToAssign => {
  const updated = { ...r };
  updated.rolesAssignment.forGovAgencyBusiness = [
    ...updated.rolesAssignment.forGovAgencyBusiness,
    newRole,
  ];
  return updated;
};

export const formatObject = (roleId: string, orgId: string) => ({
  roleId,
  orgId,
});
